import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // base
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-vt-sm text-vt-14 font-medium transition-[background-color,box-shadow,transform,color,border-color] duration-vt ease-vt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-vt-bg disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]',
  {
    variants: {
      variant: {
        // !text-white forces white text on filled-coloured-bg variants.
        // Without the bang, Radix Slot wrapping (DialogTrigger asChild) +
        // body color inheritance were letting the dark body color win the
        // cascade on the Add buttons across modules — text appeared black
        // on dark navy primary background.
        primary:
          'bg-vt-primary !text-white shadow-vt-1 hover:bg-vt-primary-600 hover:shadow-vt-2',
        secondary:
          'bg-vt-surface !text-vt-primary border border-vt-border-strong hover:bg-vt-surface-2 hover:border-vt-primary-400',
        success: 'bg-vt-safe !text-white shadow-vt-1 hover:bg-vt-safe/90 hover:shadow-vt-2',
        danger: 'bg-vt-alert !text-white shadow-vt-1 hover:bg-vt-alert/90 hover:shadow-vt-2',
        ghost: 'bg-transparent !text-vt-primary hover:bg-vt-primary-100',
        outline:
          'border border-vt-border bg-transparent !text-vt-text hover:bg-vt-surface-2 hover:border-vt-border-strong',
        link: '!text-vt-primary underline-offset-4 hover:underline'
      },
      size: {
        sm: 'h-8 px-3 text-vt-13',
        md: 'h-10 px-4',
        lg: 'h-12 px-6 text-vt-16',
        icon: 'h-9 w-9'
      }
    },
    defaultVariants: { variant: 'primary', size: 'md' }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, loadingText, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            <span>{loadingText ?? children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { buttonVariants };
